import { Tag } from "@suraasa/placebo-ui"
import {
  AssignmentCategory,
  AssignmentList,
  SUBMISSION_STATUS,
  SubmissionResultEnum,
} from "api/resources/assignments/types"

type ActionConfig = {
  canAttempt: boolean
  isOverdue: boolean
  tag: JSX.Element
  isDraft: boolean | undefined
}

export const getActionConfig = (
  item: AssignmentList,
  options?: {
    showNeutralResultTags?: boolean
  }
): ActionConfig => {
  const isDraft = item.submissionStatus === SUBMISSION_STATUS.savedForLater

  const neutralResultTag = (
    <Tag size="sm" color="primary">
      Completed
    </Tag>
  )

  if (
    item.submissionRejectionReason?.reattemptRequestApproved &&
    item.submissionStatus === SUBMISSION_STATUS.rejected
  ) {
    const isOverdue = hasMissedDeadline(item.dueDate)

    if (isOverdue) {
      return {
        isDraft,
        canAttempt: true,
        ...getDueTag(item),
      }
    }

    return {
      isDraft,
      canAttempt: true,
      isOverdue: false,
      tag: (
        <Tag size="sm" color="critical">
          Re-do
        </Tag>
      ),
    }
  }

  if (item.result === SubmissionResultEnum.PASSED) {
    return {
      isDraft,
      isOverdue: false,
      canAttempt: false,
      tag: options?.showNeutralResultTags ? (
        neutralResultTag
      ) : (
        <Tag size="sm" color="success">
          Accepted
        </Tag>
      ),
    }
  }
  if (item.result === SubmissionResultEnum.FAILED) {
    return {
      isDraft,
      isOverdue: false,
      canAttempt: false,
      tag: options?.showNeutralResultTags ? (
        neutralResultTag
      ) : (
        <Tag size="sm" color="critical">
          Rejected
        </Tag>
      ),
    }
  }

  switch (item.submissionStatus) {
    case SUBMISSION_STATUS.notReviewed:
    case SUBMISSION_STATUS.underReview:
      return {
        isDraft,
        canAttempt: false,
        isOverdue: false,
        tag: (
          <Tag size="sm" color="onSurface">
            Under review
          </Tag>
        ),
      }
    case SUBMISSION_STATUS.plagiarised:
    case SUBMISSION_STATUS.rejected:
      return {
        isDraft,
        canAttempt: false,
        isOverdue: false,
        tag: options?.showNeutralResultTags ? (
          neutralResultTag
        ) : (
          <Tag size="sm" color="critical">
            Rejected
          </Tag>
        ),
      }
  }

  return {
    isDraft,
    canAttempt: true,
    ...getDueTag(item),
  }
}

const getDueTag = (
  item: AssignmentList
): Pick<ActionConfig, "isOverdue" | "tag"> => {
  if (item.dueDate === null)
    return {
      isOverdue: false,
      tag: (
        <Tag size="sm" color="onSurface">
          Not allocated
        </Tag>
      ),
    }

  const isOverdue = hasMissedDeadline(item.dueDate)

  if (isOverdue)
    return {
      isOverdue,
      tag: (
        <Tag size="sm" color="critical">
          Deadline Missed
        </Tag>
      ),
    }

  return {
    isOverdue,
    tag: (
      <Tag size="sm" color="onSurface">
        Due
      </Tag>
    ),
  }
}

import { isAfter } from "date-fns"

export const hasMissedDeadline = (dueDate?: string | null) => {
  if (!dueDate) return false
  return isAfter(new Date(), new Date(dueDate))
}

type Params = { title: string; shortTitle: string }

export const buildAssignmentTitle = ({ title, shortTitle }: Params): string => {
  if (title === shortTitle) return title
  return `${shortTitle}: ${title}`
}

export const isResearchAssignment = (
  category?: AssignmentCategory | null,
  reattemptsAllowed?: number | null
) => {
  return category === AssignmentCategory.RESEARCH && reattemptsAllowed === -1
}
